import React, { useEffect, useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { AxiosWithAuth } from '../../Utilities/authenticationService'
import {
    Alert,
    Button,
    Box,
    Typography,
    Table,
    TableRow,
    TableCell,
    TableHead,
    TableBody,
    TextField,
    InputAdornment,
    IconButton,
    Pagination,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Chip
} from '@mui/material'
import {
    Search as SearchIcon,
    KeyboardArrowRight,
    Add as AddIcon,
    Download as DownloadIcon,
    LocationOnOutlined,
    Inventory2Outlined,
    SyncAlt as SyncAltIcon
} from '@mui/icons-material'
import { PATHS } from '../../constants'
import * as XLSX from 'xlsx'
import iconv from 'iconv-lite'
import StoreRegistrationModal from './StoreRegistrationModal'
import NewStoreModal from './NewStoreModal'

const AdminStoreManagements = ({ history }) => {
    const [searchResults, setSearchResults] = useState([])
    const [search, setSearch] = useState('')
    const [stores, setStores] = useState([])
    const [message, setMessage] = useState('')
    const [uploadError, setUploadError] = useState('')
    const [newStores, setNewStores] = useState([])
    const [newCompanyId, setNewCompanyId] = useState(null)
    const [newCompanyName, setNewCompanyName] = useState(null)
    const [page, setPage] = useState(1)
    const [rowsPerPage] = useState(20)
    const [showArchived, setShowArchived] = useState('active') // 'active', 'archived', or 'all'
    const [copyModalOpen, setCopyModalOpen] = useState(false)
    const [copySourceStore, setCopySourceStore] = useState(null)
    const [newStoreModalOpen, setNewStoreModalOpen] = useState(false)

    // Get current user info to check if admin
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}')
    const isAdmin = currentUser?.is_admin || false

    const handleSearch = value => {
        if (value === '') {
            setSearchResults([])
            return
        }
        const results = stores.filter(store => {
            return store.name.includes(value)
        })
        setSearchResults(results)
    }

    const handleSampleDownload = () => {
        const japaneseHeader = ['部署コード', '店舗名', '店舗タイプID']
        const englishHeader = ['seg_code', 'name', 'store_type_id']
        const wb = XLSX.utils.book_new()
        const data = [[...japaneseHeader], [...englishHeader]]
        const ws = XLSX.utils.aoa_to_sheet(data)
        XLSX.utils.book_append_sheet(wb, ws)
        XLSX.writeFile(wb, '店舗一括登録＿サンプル.xlsx')
    }
    const mustField = ['name', 'seg_code', 'store_type_id']
    const formatError =
        'XLSXファイルをアップロードできませんでした。ファイルのフォーマットが正しくありません。もう一度確認してください。'
    const sizeError =
        'XLSXファイルをアップロードできませんでした。ファイルが大きすぎます。10,000行以下のファイルをアップロードしてください。'
    const storePresenceError = 'XLSXファイルをアップロードできませんでした。店舗コードが既に登録されています。'
    const handleError = (key, value) => {
        if (mustField.includes(key) && !value) {
            return true
        }
        return false
    }
    const onDrop = useCallback(acceptedFiles => {
        if (acceptedFiles[0].name?.split('.')[1] !== 'xlsx') {
            setUploadError(formatError)
            return
        }
        const f = acceptedFiles[0]
        const reader = new FileReader()
        reader.onload = async evt => {
            const bstr = evt.target.result
            const wb = XLSX.read(bstr, { type: 'binary' })
            // Get first worksheet
            const wsname = wb.SheetNames[0]
            const ws = wb.Sheets[wsname]
            // Convert array of arrays
            let data = XLSX.utils.sheet_to_json(ws, { header: 1 })
            data = data.filter(item => {
                return item.length > 0
            })
            let shiftjis = false
            data[0].forEach(col => {
                if (col.includes('')) {
                    shiftjis = true
                }
            })
            // decodes with iconv and reparses if garbled characters found in first row
            if (shiftjis) {
                const decoded = iconv.decode(bstr, 'shiftjis')
                const wb = XLSX.read(decoded, { type: 'binary' })
                const ws = wb.Sheets[wb.SheetNames[0]]
                data = XLSX.utils.sheet_to_json(ws, { header: 1 })
                data = data.filter(item => {
                    return item.length > 0
                })
            }
            // error handling
            const keys = data[1]
            const segCodeIndex = keys.indexOf('seg_code')
            const segCodeList = []
            let error = false
            // check if file is too large
            if (data.length > 10000) {
                setUploadError(sizeError)
                return
            }
            // check if all required fields are present
            const allFields = mustField.every(field => {
                return keys.includes(field)
            })
            if (!allFields) {
                setUploadError(formatError)
                return
            }

            for (let i = 2; i < data.length; i++) {
                keys.forEach((key, j) => {
                    if (handleError(keys[j], data[i][j])) {
                        setUploadError(formatError)
                        error = true
                    }
                })
                if (!segCodeList.includes(data[i][segCodeIndex])) {
                    segCodeList.push(data[i][segCodeIndex])
                }
            }
            let noStores = true
            await AxiosWithAuth.get('/stores/presence/' + `${segCodeList.join(',')}`).then(res => {
                if (res?.data?.message === 'Some stores not found' || res?.data?.message === 'All stores found') {
                    setUploadError(storePresenceError)
                    noStores = false
                }
            })
            if (!noStores) {
                return
            }
            if (error) {
                return
            }
            setNewStores(data)
        }
        reader.readAsBinaryString(f)
    }, [])

    const { getRootProps, getInputProps } = useDropzone({ onDrop })

    const fetchStores = () => {
        let url = '/stores'
        if (showArchived === 'archived') {
            url = '/stores?archived=true'
        } else if (showArchived === 'all') {
            url = '/stores?archived=all'
        }
        AxiosWithAuth.get(url)
            .then(res => {
                setStores(res.data)
                setSearchResults([])
                setSearch('')
            })
            .catch(err => console.log(err))
    }

    useEffect(() => {
        fetchStores()
    }, [showArchived])

    useEffect(() => {
        if (history?.location?.state?.message) {
            setMessage(history.location.state.message)
            const newState = { ...history.location.state }
            delete newState.message
            history.replace({ ...history.location, state: newState })
        }
        if (history?.location?.state?.newCompanyId) {
            setNewCompanyId(history.location.state.newCompanyId)
            setNewCompanyName(history.location.state.newCompanyName)
        }
    }, [history.location])

    useEffect(() => {
        if (message) {
            setTimeout(() => {
                setMessage('')
            }, 5000)
        }
    }, [message])

    useEffect(() => {
        if (newStores.length === 0) return
        history.push({ pathname: PATHS.adminStoreBulkUpload, state: { data: newStores } })
    }, [newStores])

    const handleCopyClick = (store) => {
        setCopySourceStore(store)
        setCopyModalOpen(true)
    }

    const handleCopySubmit = ({ store, company }) => {
        history.push({
            pathname: PATHS.adminStoreRegistration,
            state: { store, company }
        })
    }

    const handleNewStoreCreated = (newStore) => {
        setStores(prevStores => [...prevStores, newStore])
        setMessage(`店舗「${newStore.name}」を作成しました`)
    }
    
    const storesToDisplay = search ? searchResults : stores
    const paginatedStores = storesToDisplay.slice((page - 1) * rowsPerPage, page * rowsPerPage)
    const totalPages = Math.ceil(storesToDisplay.length / rowsPerPage)

    const storesAssignedToNewCompany = stores.filter(s => s.company_id === newCompanyId).length
    const hasAssignedStores = storesAssignedToNewCompany > 0

    return (<>
            <div style={{ margin: '1rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {newCompanyId && (
                    <Alert
                        severity="info"
                        sx={{ width: '45rem', marginBottom: '1rem' }}
                        action={
                            hasAssignedStores ? (
                                <Button
                                    color="inherit"
                                    size="small"
                                    onClick={() => {
                                        history.push({
                                            pathname: PATHS.signup,
                                            state: {
                                                company_id: newCompanyId,
                                                company_name: newCompanyName
                                            }
                                        })
                                    }}
                                >
                                    ユーザー登録へ
                                </Button>
                            ) : null
                        }
                    >
                        {hasAssignedStores
                            ? `${newCompanyName}への店舗の紐づけが完了したら、ユーザー登録ページでこの会社の管理者ユーザーを作成してください。`
                            : `${newCompanyName}に店舗を紐づけてください。`
                        }
                    </Alert>
                )}
                <Box sx={{ width: '45rem', marginTop: '1rem', display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
                    {isAdmin && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setNewStoreModalOpen(true)}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            店舗登録
                        </Button>
                    )}
                </Box>
                <Box sx={{ width: '45rem', marginTop: '1rem', display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                        sx={{ flex: 1 }}
                        placeholder="店舗検索"
                        InputLabelProps={{
                            shrink: true
                        }}
                        value={search}
                        onChange={e => {
                            setSearch(e.target.value)
                            handleSearch(e.target.value)
                            setPage(1) // Reset to first page on search
                        }}
                        data-testid="store-search-box"
                        onKeyDown={e => (e.key === 'Enter' ? handleSearch(e.target.value) : null)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <IconButton>
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    <ToggleButtonGroup
                        value={showArchived}
                        exclusive
                        onChange={(e, newValue) => {
                            if (newValue !== null) {
                                setShowArchived(newValue)
                                setPage(1)
                            }
                        }}
                        size="small"
                    >
                        <ToggleButton value="active">
                            <Tooltip title="アクティブ店舗">
                                <LocationOnOutlined />
                            </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="archived">
                            <Tooltip title="アーカイブ済み店舗">
                                <Inventory2Outlined />
                            </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="all">
                            <Tooltip title="すべての店舗">
                                <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '0.75rem', px: 0.5 }}>全て</Box>
                            </Tooltip>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                {showArchived === 'archived' && (
                    <Alert severity="info" sx={{ width: '45rem', mt: 1 }}>
                        アーカイブ済み店舗を表示中
                    </Alert>
                )}
                <Table sx={{ width: '45em', marginTop: '1rem' }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>店舗名</TableCell>
                            <TableCell>会社</TableCell>
                            <TableCell align="right"></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedStores.map(store => (
                            <TableRow key={store.id} sx={{ opacity: store.archived ? 0.7 : 1 }}>
                                <TableCell>
                                    <IconButton>
                                        {store.archived ? <Inventory2Outlined color="disabled" /> : <LocationOnOutlined />}
                                    </IconButton>
                                    {store.name || ''}
                                    {store.archived && (
                                        <Chip 
                                            label="アーカイブ" 
                                            size="small" 
                                            sx={{ ml: 1, fontSize: '0.7rem' }}
                                            color="default"
                                        />
                                    )}
                                </TableCell>
                                <TableCell>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Typography variant="body2">
                                            {store.company?.company_name || '未設定'}
                                        </Typography>
                                        <Tooltip title="他社へ移管">
                                            {isAdmin && (
                                                <IconButton
                                                    onClick={() => handleCopyClick(store)}
                                                    size="small"
                                                >
                                                    <SyncAltIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Tooltip>
                                    </div>
                                    
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton
                                        onClick={() =>
                                            history.push(PATHS.managerSettingsStore.replace(':segCode', store.seg_code))
                                        }
                                    >
                                        <KeyboardArrowRight />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(event, value) => setPage(value)}
                            color="primary"
                        />
                    </Box>
                )}
                {isAdmin && (
                <div className="title-layout">
                    <div className="title-bar">
                        <Typography variant="subtitle1" margin="1rem" sx={{ width: '45rem' }}>
                            店舗の一括登録
                        </Typography>
                    </div>
                    <div
                        style={{
                            margin: '1rem',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}
                    >
                        {uploadError && (
                            <Alert
                                severity="error"
                                sx={{ width: '45rem', margin: '1rem' }}
                                onClose={() => setUploadError('')}
                            >
                                {uploadError}
                            </Alert>
                        )}
                        {message && (
                            <Alert
                                severity="success"
                                sx={{ width: '45rem', margin: '1rem', whiteSpace: 'pre-line' }}
                                onClose={() => setMessage('')}
                            >
                                {message}
                            </Alert>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', width: '48rem' }}>
                            <Button onClick={handleSampleDownload}>
                                <DownloadIcon /> XLSX形式ファイルのサンプルをダウンロードする
                            </Button>
                        </div>
                        <Box
                            sx={{
                                p: 2,
                                bgcolor: '#F5F5F5',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gridTemplateColumns: { md: '1fr 1fr' },
                                gap: 2,
                                width: '45rem',
                                height: '10rem',
                                border: '1px dashed #D9D9D9',
                                borderRadius: '2px',
                                color: '#8C8C8C',
                                '&:hover': {
                                    color: 'gray',
                                    backgroundColor: '#D9D9D9'
                                }
                            }}
                            {...getRootProps()}
                        >
                            <input {...getInputProps()} />
                            <IconButton>
                                <AddIcon />
                            </IconButton>
                            <Box>ファイルをここにドラッグ＆ドロップ</Box>
                        </Box>
                    </div>
                </div>
                )}
            </div>
            
            <StoreRegistrationModal
                open={copyModalOpen}
                onClose={() => {
                    setCopyModalOpen(false)
                    setCopySourceStore(null)
                }}
                onSubmit={handleCopySubmit}
                sourceStore={copySourceStore}
            />
            
            <NewStoreModal
                open={newStoreModalOpen}
                onClose={() => setNewStoreModalOpen(false)}
                onSuccess={handleNewStoreCreated}
            />
    </>)
}
export default AdminStoreManagements
